import { rulesetToHex, hexToRuleset } from '../utils/utils.js';
import * as Symmetry from './Symmetry.js';

/**
 * Pure ruleset algebra extracted from WorldManager: random generation, mutation,
 * canonical-group inspection, neighbor-count effective output, and inversion.
 *
 * Nothing here touches worlds, proxies, persistence, or the EventBus — the only
 * state is the precomputed `symmetryData` (canonical orbit groups) passed in once.
 * The random/mutation helpers accept an injectable `rng` (default `Math.random`)
 * so they stay deterministically testable. WorldManager keeps thin delegators so
 * its public API (and every UI caller) is unchanged.
 */
export class RulesetService {
    /**
     * @param {object} symmetryData - Output of `Symmetry.precomputeSymmetryGroups()`.
     */
    constructor(symmetryData) {
        this.symmetryData = symmetryData;
    }

    /**
     * Generate a fresh ruleset hex.
     * - `n_count`:    one random output per (centerState × neighbor-count) bucket.
     * - `totalistic`: one output per totalistic sum (centerState + active-neighbor count, 0–7) —
     *                 the classic plain-totalistic CA constraint. 8 buckets, so the whole space is
     *                 2^8 = 256 rulesets; strictly coarser than `n_count` (a totalistic ruleset is
     *                 an n_count ruleset whose (cs=1, n) bucket always equals its (cs=0, n+1) bucket).
     * - `d_sym`:      one random output per (centerState × D6 rotation/reflection orbit) group.
     * - `r_sym`:      one random output per (centerState × canonical orbit) group.
     * - default:      each of the 128 entries flipped independently.
     * @param {number} bias - Probability of a 1 output.
     * @param {string} generationMode
     * @param {() => number} [rng=Math.random]
     * @returns {string} 32-char hex
     */
    generateRandomRulesetHex(bias, generationMode, rng = Math.random) {
        const tempRuleset = new Uint8Array(128);
        if (generationMode === 'n_count') {
            for (let cs = 0; cs <= 1; cs++) {
                for (let nan = 0; nan <= 6; nan++) {
                    const out = rng() < bias ? 1 : 0;
                    for (let m = 0; m < 64; m++) if (Symmetry.countSetBits(m) === nan) tempRuleset[(cs << 6) | m] = out;
                }
            }
        } else if (generationMode === 'totalistic') {
            for (let sum = 0; sum <= 7; sum++) {
                const out = rng() < bias ? 1 : 0;
                for (let cs = 0; cs <= 1; cs++) {
                    for (let m = 0; m < 64; m++) {
                        if (cs + Symmetry.countSetBits(m) === sum) tempRuleset[(cs << 6) | m] = out;
                    }
                }
            }
        } else if (generationMode === 'r_sym' || generationMode === 'd_sym') {
            const groups = this._groupsForMode(generationMode);
            if (!groups) {
                console.warn(`generateRandomRulesetHex: symmetryData not available for ${generationMode}, falling back to random.`);
                for (let i = 0; i < 128; i++) tempRuleset[i] = rng() < bias ? 1 : 0;
            } else {
                tempRuleset.fill(0);
                for (const group of groups) {
                    for (let cs = 0; cs <= 1; cs++) {
                        const out = rng() < bias ? 1 : 0;
                        for (const member of group.members) tempRuleset[(cs << 6) | member] = out;
                    }
                }
            }
        } else {
            for (let i = 0; i < 128; i++) tempRuleset[i] = rng() < bias ? 1 : 0;
        }
        return rulesetToHex(tempRuleset);
    }

    /**
     * Produce a mutated copy of `sourceHex`.
     * - `single`:     flip each of the 128 entries with probability `mutationRate`.
     * - `d_sym`:      flip whole D6 rotation/reflection orbit groups (per centerState).
     * - `r_sym`:      flip whole canonical orbit groups (per centerState).
     * - `n_count`:    flip whole neighbor-count buckets, seeded from `referenceRuleset`'s
     *                 effective output (matches the legacy behaviour where the n_count
     *                 mode read the *selected* world's effective rule, not the source).
     * - `totalistic`: flip whole totalistic-sum buckets (centerState + active-neighbor count,
     *                 0–7), seeded from `referenceRuleset`'s effective output exactly like `n_count`.
     * @param {string} sourceHex
     * @param {number} mutationRate
     * @param {string} mutationMode
     * @param {Uint8Array|null} [referenceRuleset=null] - Parsed ruleset whose effective
     *        per-neighbor-count output seeds `n_count` flips. Falls back to "mixed" (2).
     * @param {() => number} [rng=Math.random]
     * @returns {string} 32-char hex
     */
    generateMutatedHex(sourceHex, mutationRate, mutationMode, referenceRuleset = null, rng = Math.random) {
        if ((mutationMode === 'r_sym' || mutationMode === 'd_sym') && !this._groupsForMode(mutationMode)) return sourceHex;
        const rules = hexToRuleset(sourceHex);
        this._mutateUnitsInPlace(rules, mutationRate, mutationMode, referenceRuleset, rng);
        return rulesetToHex(rules);
    }

    /** The canonical orbit groups, or null when symmetryData is unavailable. */
    _canonicalGroups() {
        const groups = this.symmetryData && this.symmetryData.canonicalRepresentatives;
        return groups && groups.length > 0 ? groups : null;
    }

    /** The C6 or D6 orbit groups for a symmetry mode, or null when unavailable/unknown. */
    _groupsForMode(mode) {
        if (mode === 'r_sym') return this._canonicalGroups();
        if (mode === 'd_sym') {
            const groups = this.symmetryData && this.symmetryData.dihedralCanonicalRepresentatives;
            return groups && groups.length > 0 ? groups : null;
        }
        return null;
    }

    /**
     * Shared mutation core: flip whole inheritance units of `rules` in place, per `mode` (the unit
     * shapes and draw order documented on {@link generateMutatedHex}). `single` flips per-bit;
     * unknown modes mutate nothing. Used by both mutation and the post-crossover mutation so a
     * mode-constrained ruleset stays inside its mode's subspace.
     * @param {Uint8Array} rules
     * @param {number} mutationRate
     * @param {string} mode
     * @param {Uint8Array|null} referenceRuleset - Seeds `n_count`/`totalistic` flips (2 = mixed ⇒
     *        extra `Math.round(rng())` draw).
     * @param {() => number} rng
     */
    _mutateUnitsInPlace(rules, mutationRate, mode, referenceRuleset, rng) {
        if (mode === 'r_sym' || mode === 'd_sym') {
            const symmetryGroups = this._groupsForMode(mode);
            if (!symmetryGroups) return;

            for (const group of symmetryGroups) {
                for (let cs = 0; cs <= 1; cs++) {
                    if (rng() < mutationRate) {
                        const currentOutput = rules[(cs << 6) | group.representative];
                        const newOutput = 1 - currentOutput;
                        for (const member of group.members) {
                            const idx = (cs << 6) | member;
                            rules[idx] = newOutput;
                        }
                    }
                }
            }
        } else if (mode === 'n_count') {
            for (let cs = 0; cs <= 1; cs++) {
                for (let nan = 0; nan <= 6; nan++) {
                    if (rng() < mutationRate) {
                        const currentEffectiveOutput = RulesetService.getEffectiveRuleForNeighborCount(referenceRuleset, cs, nan);
                        const newOutput = (currentEffectiveOutput === 2) ? Math.round(rng()) : 1 - currentEffectiveOutput;

                        for (let mask = 0; mask < 64; mask++) {
                            if (Symmetry.countSetBits(mask) === nan) {
                                const idx = (cs << 6) | mask;
                                rules[idx] = newOutput;
                            }
                        }
                    }
                }
            }
        } else if (mode === 'totalistic') {
            for (let sum = 0; sum <= 7; sum++) {
                if (rng() < mutationRate) {
                    const currentEffectiveOutput = RulesetService.getEffectiveRuleForTotalisticSum(referenceRuleset, sum);
                    const newOutput = (currentEffectiveOutput === 2) ? Math.round(rng()) : 1 - currentEffectiveOutput;

                    for (let cs = 0; cs <= 1; cs++) {
                        for (let mask = 0; mask < 64; mask++) {
                            if (cs + Symmetry.countSetBits(mask) === sum) {
                                rules[(cs << 6) | mask] = newOutput;
                            }
                        }
                    }
                }
            }
        } else if (mode === 'single') {
            for (let i = 0; i < 128; i++) {
                if (rng() < mutationRate) {
                    rules[i] = 1 - rules[i];
                }
            }
        }
        // Unknown modes mutate nothing (matches the pre-extraction behaviour).
    }

    /**
     * Breed two parent ruleset hexes into a child (Phase 5 of the auto-explore roadmap).
     * - `uniform`: per-bit coin flip — each of the 128 outputs is taken from A or B independently.
     * - `d_sym`:   per `(centerState × D6 orbit)` group, pick one parent wholesale.
     * - `r_sym`:   per `(centerState × canonical orbit)` group, pick one parent wholesale and copy
     *              its outputs across every orbit member, so coherent rule families travel together
     *              (exactly like r_sym mutation/generation). Falls back to `uniform` if symmetryData
     *              is unavailable.
     * - `n_count`: per `(centerState × neighbor-count bucket)`, pick one parent wholesale and copy
     *              its outputs across every mask with that active-neighbor count, so totalistic
     *              behaviour is inherited as a unit (mirrors n_count mutation/generation).
     * - `totalistic`: per totalistic-sum bucket (centerState + active-neighbor count, 0–7), pick one
     *              parent wholesale and copy its outputs across the whole bucket.
     * An optional low post-crossover mutation rate injects fresh variation the way breeding
     * pipelines usually do — flipping the same inheritance units the mode recombines (whole orbit
     * groups for `r_sym`, buckets for `n_count`/`totalistic`, per-bit for `uniform`), so the child
     * stays inside the mode's subspace. With `postMutationRate=0` every child bit is taken verbatim
     * from A or B (and breeding identical parents is the identity).
     * Pure — the injectable `rng` keeps it deterministically testable like the rest of the algebra.
     * @param {string} hexA
     * @param {string} hexB
     * @param {'uniform'|'d_sym'|'r_sym'|'n_count'|'totalistic'} [mode='r_sym']
     * @param {() => number} [rng=Math.random]
     * @param {number} [postMutationRate=0] - Post-crossover flip probability per inheritance unit
     *        (the unit follows `mode`: orbit group / count bucket / sum bucket / single bit).
     * @returns {string} 32-char hex
     */
    crossoverHexes(hexA, hexB, mode = 'r_sym', rng = Math.random, postMutationRate = 0) {
        return this.crossoverPoolHexes([hexA, hexB], mode, rng, postMutationRate);
    }

    /**
     * Recombine a *pool* of parent ruleset hexes into a single child (the genepool generalization of
     * `crossoverHexes`). For each inheritance unit a parent is drawn uniformly at random from the pool
     * and its outputs copied across that whole unit:
     * - `uniform`: per-bit.
     * - `d_sym`:   per `(centerState × D6 orbit)` group. Falls back to `uniform` if symmetryData
     *              is unavailable.
     * - `r_sym`:   per `(centerState × canonical orbit)` group. Falls back to `uniform` if symmetryData
     *              is unavailable.
     * - `n_count`: per `(centerState × neighbor-count bucket)`.
     * - `totalistic`: per totalistic-sum bucket (centerState + active-neighbor count, 0–7).
     * With a single parent every unit is taken from it (so a 1-parent pool + `postMutationRate` is
     * exactly clone-and-mutate). With two parents the rng-draw order and parent choice are identical to
     * the binary `crossoverHexes` (`floor(rng()*2)` matches `rng() < 0.5`), so existing callers/tests
     * are byte-identical. The injectable `rng` keeps it deterministic.
     * @param {string[]} hexes - One or more 32-char ruleset hexes.
     * @param {'uniform'|'d_sym'|'r_sym'|'n_count'|'totalistic'} [mode='r_sym']
     * @param {() => number} [rng=Math.random]
     * @param {number} [postMutationRate=0] - Post-crossover flip probability per inheritance unit
     *        (the unit follows `mode`: orbit group / count bucket / sum bucket / single bit).
     * @returns {string} 32-char hex ('Error'/empty pool yields a zeroed ruleset).
     */
    crossoverPoolHexes(hexes, mode = 'r_sym', rng = Math.random, postMutationRate = 0) {
        const parents = (hexes || []).map(hexToRuleset);
        const child = new Uint8Array(128);
        if (parents.length === 0) return rulesetToHex(child);

        // Draw a parent uniformly from the pool; clamp guards an injected rng that returns exactly 1.
        const pickParent = () => parents[Math.min(parents.length - 1, Math.floor(rng() * parents.length))];

        const symmetryGroups = this._groupsForMode(mode);
        if ((mode === 'r_sym' || mode === 'd_sym') && symmetryGroups) {
            for (const group of symmetryGroups) {
                for (let cs = 0; cs <= 1; cs++) {
                    const parent = pickParent();
                    for (const member of group.members) {
                        const idx = (cs << 6) | member;
                        child[idx] = parent[idx];
                    }
                }
            }
        } else if (mode === 'n_count') {
            for (let cs = 0; cs <= 1; cs++) {
                for (let nan = 0; nan <= 6; nan++) {
                    const parent = pickParent();
                    for (let mask = 0; mask < 64; mask++) {
                        if (Symmetry.countSetBits(mask) === nan) {
                            const idx = (cs << 6) | mask;
                            child[idx] = parent[idx];
                        }
                    }
                }
            }
        } else if (mode === 'totalistic') {
            for (let sum = 0; sum <= 7; sum++) {
                const parent = pickParent();
                for (let cs = 0; cs <= 1; cs++) {
                    for (let mask = 0; mask < 64; mask++) {
                        if (cs + Symmetry.countSetBits(mask) === sum) {
                            const idx = (cs << 6) | mask;
                            child[idx] = parent[idx];
                        }
                    }
                }
            }
        } else {
            // uniform (and unavailable symmetry-mode fallbacks): per-bit draw from the pool.
            for (let i = 0; i < 128; i++) {
                child[i] = pickParent()[i];
            }
        }

        if (postMutationRate > 0) {
            // The post-crossover mutation flips the SAME inheritance units the crossover treats as
            // atomic genes (per-bit for uniform / an unavailable symmetry mode), so a constrained child
            // never picks up a unit-breaking stray bit — each mode's subspace is closed under
            // breeding (auto-explore relies on this).
            const unitMode = ((mode === 'r_sym' || mode === 'd_sym') && symmetryGroups) || mode === 'n_count' || mode === 'totalistic'
                ? mode : 'single';
            this._mutateUnitsInPlace(child, postMutationRate, unitMode, child, rng);
        }

        return rulesetToHex(child);
    }

    /**
     * Project a ruleset hex onto a constraint mode's subspace by majority vote over each of the
     * mode's inheritance units (`r_sym`: C6 orbit group × centerState; `d_sym`: D6 orbit group ×
     * centerState; `n_count`:
     * neighbor-count bucket × centerState; `totalistic`: totalistic-sum bucket). Ties take the
     * unit's first (lowest-index) entry, so the projection is deterministic; a ruleset already in
     * the subspace is returned bit-identical. `single`/unknown modes (and symmetry modes without
     * symmetryData) return the hex unchanged. Auto-explore uses this to coerce a free-form seed
     * ruleset into the selected search mode before mutating it — unit-wise mutation flips whole
     * units but never repairs asymmetry the seed already carries.
     * @param {string} hex
     * @param {string} mode
     * @returns {string} 32-char hex
     */
    projectToMode(hex, mode) {
        const units = this._unitsForMode(mode);
        if (!units) return hex;
        const rules = hexToRuleset(hex);
        for (const unit of units) {
            let ones = 0;
            for (const idx of unit) ones += rules[idx];
            const out = ones * 2 > unit.length ? 1 : ones * 2 < unit.length ? 0 : rules[unit[0]];
            for (const idx of unit) rules[idx] = out;
        }
        return rulesetToHex(rules);
    }

    /**
     * The inheritance units (arrays of rule indices) a constraint mode treats as atomic genes, or
     * null for `single`/unknown modes (and symmetry modes without symmetryData) — i.e. no constraint.
     * @param {string} mode
     * @returns {number[][]|null}
     */
    _unitsForMode(mode) {
        if (mode === 'r_sym' || mode === 'd_sym') {
            const groups = this._groupsForMode(mode);
            if (!groups) return null;
            const units = [];
            for (const group of groups) {
                for (let cs = 0; cs <= 1; cs++) {
                    units.push(group.members.map((m) => (cs << 6) | m));
                }
            }
            return units;
        }
        if (mode === 'n_count') {
            const units = [];
            for (let cs = 0; cs <= 1; cs++) {
                for (let nan = 0; nan <= 6; nan++) {
                    const unit = [];
                    for (let mask = 0; mask < 64; mask++) {
                        if (Symmetry.countSetBits(mask) === nan) unit.push((cs << 6) | mask);
                    }
                    units.push(unit);
                }
            }
            return units;
        }
        if (mode === 'totalistic') {
            const units = [];
            for (let sum = 0; sum <= 7; sum++) {
                const unit = [];
                for (let cs = 0; cs <= 1; cs++) {
                    for (let mask = 0; mask < 64; mask++) {
                        if (cs + Symmetry.countSetBits(mask) === sum) unit.push((cs << 6) | mask);
                    }
                }
                units.push(unit);
            }
            return units;
        }
        return null;
    }

    /**
     * The effective output shared by every mask with `numActiveNeighbors` set bits
     * (for a given centerState), or 2 ("mixed") if the outputs disagree or the
     * ruleset is missing/invalid.
     * @param {Uint8Array|null} ruleset
     * @param {number} centerState
     * @param {number} numActiveNeighbors
     * @returns {0|1|2}
     */
    static getEffectiveRuleForNeighborCount(ruleset, centerState, numActiveNeighbors) {
        if (!ruleset) return 2;
        let firstOutput = -1;
        for (let mask = 0; mask < 64; mask++) {
            if (Symmetry.countSetBits(mask) === numActiveNeighbors) {
                const output = ruleset[(centerState << 6) | mask];
                if (firstOutput === -1) firstOutput = output;
                else if (firstOutput !== output) return 2;
            }
        }
        return firstOutput === -1 ? 2 : firstOutput;
    }

    /**
     * The effective output shared by every rule entry whose totalistic sum
     * (centerState + active-neighbor count) equals `sum`, or 2 ("mixed") if the
     * outputs disagree or the ruleset is missing/invalid.
     * @param {Uint8Array|null} ruleset
     * @param {number} sum - Totalistic sum in [0, 7].
     * @returns {0|1|2}
     */
    /**
     * Write `outputState` to every rule entry in one totalistic bucket, in place.
     *
     * A bucket spans BOTH centre states — `(cs=1, n)` and `(cs=0, n+1)` share a total live count and
     * must agree — which is exactly what makes `totalistic` stricter than `n_count`. Driving all 8
     * buckets therefore lands any rule in the totalistic subspace, which is what the editor's
     * Totalistic mode relies on.
     * @param {Uint8Array} rules 128-entry table, mutated in place.
     * @param {number} sum Totalistic sum in [0, 7].
     * @param {0|1} outputState
     * @returns {Uint8Array} `rules`, for chaining.
     */
    static setRulesForTotalisticSum(rules, sum, outputState) {
        for (let cs = 0; cs <= 1; cs++) {
            for (let mask = 0; mask < 64; mask++) {
                if (cs + Symmetry.countSetBits(mask) !== sum) continue;
                rules[(cs << 6) | mask] = outputState;
            }
        }
        return rules;
    }

    static getEffectiveRuleForTotalisticSum(ruleset, sum) {
        if (!ruleset) return 2;
        let firstOutput = -1;
        for (let cs = 0; cs <= 1; cs++) {
            for (let mask = 0; mask < 64; mask++) {
                if (cs + Symmetry.countSetBits(mask) !== sum) continue;
                const output = ruleset[(cs << 6) | mask];
                if (firstOutput === -1) firstOutput = output;
                else if (firstOutput !== output) return 2;
            }
        }
        return firstOutput === -1 ? 2 : firstOutput;
    }

    /**
     * For each canonical orbit group, the effective output for centerState 0 and 1
     * (2 when the group's members disagree). Drives the ChromaLab / symmetry editor.
     * @param {Uint8Array|null} ruleset
     * @returns {Array<object>}
     */
    getCanonicalRuleDetails(ruleset, mode = 'r_sym') {
        if (!this.symmetryData) {
            console.error("getCanonicalRuleDetails: symmetryData is undefined.");
            return [];
        }
        if (!ruleset) return [];

        const groups = this._groupsForMode(mode);
        if (!groups) return [];

        return groups.flatMap(group => {
            let outputState0 = -1, outputState1 = -1;
            let mixed0 = false, mixed1 = false;

            for (const member of group.members) {
                const currentOut0 = ruleset[(0 << 6) | member];
                if (outputState0 === -1) outputState0 = currentOut0;
                else if (outputState0 !== currentOut0) mixed0 = true;

                const currentOut1 = ruleset[(1 << 6) | member];
                if (outputState1 === -1) outputState1 = currentOut1;
                else if (outputState1 !== currentOut1) mixed1 = true;
            }
            return [
                { canonicalBitmask: group.representative, centerState: 0, orbitSize: group.orbitSize, effectiveOutput: mixed0 ? 2 : outputState0, members: group.members },
                { canonicalBitmask: group.representative, centerState: 1, orbitSize: group.orbitSize, effectiveOutput: mixed1 ? 2 : outputState1, members: group.members }
            ];
        });
    }

    /**
     * Bitwise-invert every output in a ruleset hex (1↔0 across all 128 entries).
     * @param {string} hex
     * @returns {string} 32-char hex
     */
    static invertHex(hex) {
        const rulesetArray = hexToRuleset(hex);
        for (let i = 0; i < rulesetArray.length; i++) {
            rulesetArray[i] = 1 - rulesetArray[i];
        }
        return rulesetToHex(rulesetArray);
    }
}
